import {
  Driver,
  MovementSource,
} from './../nav-processing/mappings/mappings.model';
import { Injectable, enableProdMode } from '@angular/core';
import { PeyeService } from './peye/peye.service';
import { VisionService } from './vision-web/vision.service';
import { DriverTemplate } from './driver-template';
import { MappingsService } from '../nav-processing/mappings/mappings.service';
import { EyeTrackerService } from './tobii/eye-tracker.service';
import { RastreadorOcularService } from './rastreador/rastreador.service';
import { MouseService } from './mouse/mouse.service';
import { GazeWebcamService } from './gaze-webcam/gaze-webcam.service';

declare const ngDevMode: boolean;
const log = (...args: any[]) => {
  if (ngDevMode) {console.log(...args);}
};

export interface DriverControl {
  driver: Driver; // The driver type
  service: DriverTemplate; // The driver service
  // service.enabled: boolean; // Is the driver enabled (true if it should be running)
  // service.running: boolean; // Is the driver running now?
  // service.capturing: number; // Is the driver capturing valid user data points now?
}

@Injectable({
  providedIn: 'root',
})
export class NavDriverService {
  private driverMonitorInterval: NodeJS.Timeout;
  private driverControlArray: DriverControl[] = [];


  /**
   * HOW TO CREATE A DRIVER
   * The services implementing each driver must be injected in this constructor
   * using the same name as the driver keys defined on the Driver enum (mappings.model.ts)
   */
  constructor(
    public eyeTracker: EyeTrackerService,
    public peyeTracker: PeyeService,
    public rastreador: RastreadorOcularService,
    public face: VisionService,
    public mappings: MappingsService,
    public mouse: MouseService,
    public gazeWeb: GazeWebcamService,
  ) {

    this.driverMonitorInterval = setInterval(() => {
      this.driverMonitor();
    }, 5000);
  }

  driverMonitor() {
    //log('Driver Monitor', this.driverControlArray);
    this.driverControlArray.forEach((control) => {
      //console.log('[driverMonitor] Driver[control.driver]', control.driver);
      if(control.service) {
       // log('Driver[control.driver] enabled', control.service.isEnabled());
        if (control.service.isEnabled()) {
          if (!control.service.isRunning()) {
            log('[driverMonitor] Starting Driver', this.driverControlArray.map((control) => Driver[control.driver]));
            control.service.start(this.driverControlArray);
          }
        } else {
          if (control.service.isRunning()) {
            control.service.stop();
          }
        }
      }
    });
  }

  /**
   * Check if the driver is active
   *
   * @param driver
   * @returns true if driver is registered or already registered, false if not
   */
  registerDriver(driver: Driver, enabled: boolean = false) {
    log('Registering Driver', Driver[driver], enabled);
    if (!this.isDriverRegistered(driver)) {
      const len = this.driverControlArray.push({
        driver,
        service: this.getDriverService(driver),
      });
      // Starts the service if it should be enabled
      this.driverControlArray[len - 1].service?.start();

      return true;
    }
    return false;
  }

  /**
   * Disabled a registered driver
   *
   * @param driver
   *
   */
  disableDriver(driver: Driver) {
    return this.driverControlArray
      .find((control) => control.driver === driver)
      ?.service.stop();
  }

  /**
   * Removes driver registered
   *
   * @param driver
   *
   */
  unregisterDriver(driver: Driver) {
    // stop service
    this.driverControlArray.find((control) => control.driver === driver)?.service.stop();
    this.driverControlArray = this.driverControlArray.filter(
      (control) => control.driver !== driver
    );
  }

  /**
   * Check if the driver is registered
   *
   * @param driver
   * @returns true if driver is registered
   */
  isDriverRegistered(driver: Driver) {
    return this.driverControlArray.find((control) => control.driver === driver);
  }

  /**
   * Check if the driver is enabled
   *
   * @param driver
   * @returns true if driver is enabled
   */
  isDriverEnabled(driver: Driver) {
    return this.driverControlArray
      .find((control) => control.driver === driver)
      ?.service.isEnabled();
  }

  /**
   * Check if the driver is running
   *
   * @param driver
   * @returns true if driver is running
   */
  isDriverRunning(driver: Driver) {
    return this.driverControlArray
      .find((control) => control.driver === driver)
      ?.service.isRunning();
  }

  /**
   * Check if the driver is capturing
   *
   * @param driver
   * @returns true if driver is capturing
   */
  isDriverCapturing(driver: Driver) {
    return this.driverControlArray
      .find((control) => control.driver === driver)
      ?.service.isCapturing();
  }

  /**
   * Get the array of all regisred drivers
   *
   * @returns the driver array
   */
  getRegisteredDrivers(): Driver[] {
    return this.driverControlArray.map((control) => control.driver);
  }

  /**
   * Updates Driver Register
   * drivers on the list will be registered or remain registered
   * drivers currently registered but not on the list will be unregistered
   *
   * @param driver array of drivers
   */
  updateDriverRegistry(drivers: Driver[], enableAll = false) {
    this.driverControlArray?.forEach((control) => {
      if (!drivers.includes(control.driver)) {
        this.unregisterDriver(control.driver);
      }
      else if (enableAll) {
        //console.log('enableAll', control.driver, Driver[control.driver]);
        control.service.start(this.driverControlArray);
      }
    });
    drivers.forEach((driver) => {
      this.registerDriver(driver, enableAll);
    });
    console.log('updateDriverRegistry', this.driverControlArray.map((control) => Driver[control.driver]));
  }

  /**
   * Get the driver service
   * the services implementing each driver must be injected in the constructor
   * using the same name as the driver keys defined on the Driver enum
   *
   * @param driver
   * @returns the driver service
   */
  private getDriverService(driver: Driver) {
    const driverServiceName = Driver[driver];
    //log('Getting Driver Service', driverServiceName);
    if (this.hasOwnProperty(driverServiceName)) {
      return this[driverServiceName];
    } else {
      log('[nav-driver service] Driver Service not found', driverServiceName);
      return undefined;
    }
  }
}
